import React, { useState, useEffect } from 'react';
import { ArrowRight, Plus } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../../contexts/auth/hooks';
import { supabase } from '../../../lib/supabase';
import { Service, ServiceFormData } from './types';
import { validateServiceForm } from './validation';
import { ServiceForm } from './components/ServiceForm';
import { ServiceCard } from './components/ServiceCard';
import toast from 'react-hot-toast';

const defaultFormData: ServiceFormData = {
  name_he: '',
  price: '',
  duration: '30',
  image: null,
  description: '',
  promotion: undefined
};

function BusinessServices() {
  const { user, business, loading: authLoading } = useAuth();
  const [services, setServices] = useState<Service[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingService, setEditingService] = useState<Service | null>(null);
  const [formData, setFormData] = useState<ServiceFormData>(defaultFormData);

  useEffect(() => {
    const loadServices = async () => {
      if (authLoading) return;

      if (!user?.id) {
        setLoading(false);
        return;
      }

      try {
        let businessId = business?.id;
        if (!businessId) {
          const { data: userData, error: userError } = await supabase
            .from('users')
            .select('business_id')
            .eq('id', user.id)
            .single();

          if (userError) throw userError;
          businessId = userData?.business_id;
        }

        if (!businessId) {
          throw new Error('לא נמצא עסק מקושר');
        }

        const { data, error } = await supabase
          .from('services')
          .select('*')
          .eq('business_id', businessId)
          .order('created_at');

        if (error) throw error;
        setServices(data || []);
      } catch (error: any) {

        toast.error('שגיאה בטעינת השירותים');
      } finally {
        setLoading(false);
      }
    };

    loadServices();
  }, [user?.id, business?.id, authLoading]);

  const uploadImage = async (file: File, businessId: string): Promise<string | null> => {
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${Math.random().toString(36).substring(2)}${Date.now()}.${fileExt}`;
      const filePath = `${businessId}/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('services')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      const { data } = supabase.storage
        .from('services')
        .getPublicUrl(filePath);

      return data.publicUrl;
    } catch (error) {

      throw new Error('שגיאה בהעלאת התמונה');
    }
  };

  const handleEdit = (service: Service) => {
    setEditingService(service);
    
    // חילוץ מספר הדקות מה-interval
    const durationMatch = service.duration.match(/(\d+):(\d+):(\d+)/);
    const minutes = durationMatch 
      ? parseInt(durationMatch[1]) * 60 + parseInt(durationMatch[2])
      : 30; // ברירת מחדל אם יש בעיה בפירוק ה-interval

    setFormData({
      name_he: service.name_he,
      price: service.price.toString(),
      duration: minutes.toString(),
      description: service.description || '',
      promotion: service.promotion
      // לא מאתחלים את שדה התמונה בכלל כשעורכים שירות קיים
    });
    setShowForm(true);
    // גלול לראש האלמנט של הטופס (ולא רק ל-top של הדף)
    setTimeout(() => {
      const formEl = document.getElementById('service-form-popup');
      if (formEl) {
        formEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } else {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }, 0);
  };

  const handleSubmit = async () => {
    const validationError = validateServiceForm(formData);
    if (validationError) {
      toast.error(validationError);
      return;
    }

    try {
      let businessId = business?.id;
      
      if (!businessId) {
        const { data: userData, error: userError } = await supabase
          .from('users')
          .select('business_id')
          .eq('id', user?.id)
          .single();

        if (userError) throw new Error('לא נמצא עסק מקושר');
        businessId = userData?.business_id;
      }

      if (!businessId) {
        throw new Error('לא נמצא עסק מקושר');
      }

      let imageUrl = editingService?.image_url;

      // טיפול בתמונה רק אם יש שינוי בשדה התמונה
      if ('image' in formData) {
        // אם ביקשנו במפורש למחוק את התמונה
        if (formData.image === null) {
          if (editingService?.image_url) {
            const oldPath = editingService.image_url.split('/').pop();
            if (oldPath) {
              await supabase.storage
                .from('services')
                .remove([`${businessId}/${oldPath}`]);
            }
          }
          imageUrl = null;
        }
        // אם יש תמונה חדשה להעלות
        else if (formData.image instanceof File) {
          // מחיקת התמונה הקיימת אם יש
          if (editingService?.image_url) {
            const oldPath = editingService.image_url.split('/').pop();
            if (oldPath) {
              await supabase.storage
                .from('services')
                .remove([`${businessId}/${oldPath}`]);
            }
          }
          // העלאת התמונה החדשה
          imageUrl = await uploadImage(formData.image, businessId);
        }
      }

      // חילוץ מספר הדקות מהטופס והמרה ל-interval
      const minutes = parseInt(formData.duration);
      const hours = Math.floor(minutes / 60);
      const remainingMinutes = minutes % 60;
      const duration = `${hours.toString().padStart(2, '0')}:${remainingMinutes.toString().padStart(2, '0')}:00`;

      const serviceData = {
        business_id: businessId,
        name: formData.name_he,
        name_he: formData.name_he,
        price: parseFloat(formData.price),
        duration,
        image_url: imageUrl,
        description: formData.description,
        promotion: formData.promotion
      };

      if (editingService) {
        const { error } = await supabase
          .from('services')
          .update(serviceData)
          .eq('id', editingService.id);

        if (error) throw error;
        toast.success('השירות עודכן בהצלחה');
      } else {
        const { error } = await supabase
          .from('services')
          .insert([serviceData]);

        if (error) throw error;
        toast.success('השירות נוסף בהצלחה');
      }

      setShowForm(false);
      setEditingService(null);
      setFormData(defaultFormData);
      
      const { data, error } = await supabase
        .from('services')
        .select('*')
        .eq('business_id', businessId)
        .order('created_at');

      if (error) throw error;
      setServices(data || []);
    } catch (error: any) {

      toast.error(error.message || (editingService ? 'שגיאה בעדכון השירות' : 'שגיאה בהוספת השירות'));
    }
  };

  const handlePromotionUpdate = async (serviceId: string, promotion: any) => {
    try {
      const { error } = await supabase
        .from('services')
        .update({ promotion })
        .eq('id', serviceId);

      if (error) throw error;

      toast.success(promotion ? 'המבצע עודכן בהצלחה' : 'המבצע הוסר בהצלחה');

      const { data, error: loadError } = await supabase
        .from('services')
        .select('*')
        .eq('business_id', business?.id)
        .order('created_at');

      if (loadError) throw loadError;
      setServices(data || []);
    } catch (error) {
      toast.error('שגיאה בעדכון המבצע');
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('האם אתה בטוח שברצונך למחוק את השירות?')) return;

    try {
      const service = services.find(s => s.id === id);

      if (service?.image_url) {
        const businessId = business?.id;
        const oldPath = service.image_url.split('/').pop();
        if (businessId && oldPath) {
          await supabase.storage
            .from('services')
            .remove([`${businessId}/${oldPath}`]);
        }
      }

      const { error } = await supabase
        .from('services')
        .delete()
        .eq('id', id);

      if (error) throw error;
      toast.success('השירות נמחק בהצלחה');

      const { data, error: loadError } = await supabase
        .from('services')
        .select('*')
        .eq('business_id', business?.id)
        .order('created_at');

      if (loadError) throw loadError;
      setServices(data || []);
    } catch (error) {
      toast.error('שגיאה במחיקת השירות');
    }
  };

  useEffect(() => {
    const checkLocalStorageForServices = async () => {
      const registrationDraft = localStorage.getItem('registration_draft');

      if (registrationDraft) {
        try {
          const parsedDraft = JSON.parse(registrationDraft);
          const services = parsedDraft.step4?.services;

          if (Array.isArray(services) && services.length > 0) {
            let businessId = business?.id;

            if (!businessId) {
              const { data: userData, error: userError } = await supabase
                .from('users')
                .select('business_id')
                .eq('id', user?.id)
                .single();

              if (userError) throw userError;
              businessId = userData?.business_id;
            }

            if (!businessId) {
              throw new Error('לא נמצא עסק מקושר');
            }

            for (const service of services) {
              const serviceData = {
                business_id: businessId,
                name: service.name_he,
                name_he: service.name_he,
                price: parseFloat(service.price),
                duration: service.duration,
                description: service.description || '',
                image_url: null, // Assuming no image is provided
                promotion: null
              };

              const { error } = await supabase
                .from('services')
                .insert([serviceData]);

              if (error) throw error;
            }

            toast.success('השירותים נוספו בהצלחה');
            localStorage.removeItem('registration_draft');

            const { data, error: loadError } = await supabase
              .from('services')
              .select('*')
              .eq('business_id', businessId)
              .order('created_at');

            if (loadError) throw loadError;
            setServices(data || []);
          }
        } catch (error) {

        }
      }
    };

    checkLocalStorageForServices();
  }, [business, user]);

  if (loading) {
    return (
      <div className="min-h-[400px] flex items-center justify-center pb-24">
        <div className="w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col min-h-0 bg-gray-50">
      <div className="flex-1 overflow-y-auto pb-24">
        <div className="sticky top-0 z-10 bg-gray-50 px-4 pt-4 pb-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Link to="/settings" className="text-gray-500 active:text-gray-700">
                <ArrowRight className="h-6 w-6" />
              </Link>
              <h1 className="text-2xl font-bold text-gray-900">שירותים</h1>
            </div>
            <button
              onClick={() => {
                setEditingService(null);
                setFormData(defaultFormData);
                setShowForm(true);
                setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 0);
              }}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg active:bg-blue-700 transition-colors"
            >
              <Plus className="h-5 w-5" />
              <span className="text-sm font-medium">הוסף</span>
            </button>
          </div>
        </div>

        {showForm && (
          <ServiceForm
            formData={formData}
            onFormChange={setFormData}
            onSubmit={handleSubmit}
            onCancel={() => {
              setShowForm(false);
              setEditingService(null);
              setFormData(defaultFormData);
            }}
            isEditing={!!editingService}
            existingImageUrl={editingService?.image_url}
          />
        )}

        {services.length > 0 ? (
          <div className="px-4 pt-2 space-y-3">
            {services.map((service) => (
              <ServiceCard
                key={service.id}
                service={service}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onPromotionUpdate={handlePromotionUpdate}
              />
            ))}
          </div>
        ) : !showForm && (
          <div className="flex flex-col items-center justify-center py-16 px-4">
            <div className="text-center space-y-3">
              <p className="text-gray-500 text-sm">לא נמצאו שירותים</p>
              <button
                onClick={() => {
                  setEditingService(null);
                  setFormData(defaultFormData);
                  setShowForm(true);
                }}
                className="text-blue-600 active:text-blue-700 text-sm font-medium"
              >
                הוסף שירות חדש
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default BusinessServices;